import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, BookOpenText, Clapperboard, ExternalLink, Headphones, Play, Radio, RefreshCw, ScanLine, Users, X } from 'lucide-react';
import { demoScenario } from '../data/demoScenario.js';
import { DIRECTOR_PROTOCOL_URL, DIRECTOR_STARTUP_GRACE_MS } from '../core/directorLaunch.js';
import { BackgroundMusic } from './BackgroundMusic.jsx';

export function HomeScreen({ canHost = true, onCreate, onJoin, onPractice, onLibrary, onSettings, onImport }) {
  const windowsDesktop = /Windows NT/i.test(globalThis.navigator?.userAgent || '');
  const [directorLaunch, setDirectorLaunch] = useState('idle');
  const launchTimer = useRef(null);

  useEffect(() => () => window.clearTimeout(launchTimer.current), []);

  function openDirector(event) {
    event?.preventDefault();
    window.clearTimeout(launchTimer.current);
    setDirectorLaunch('opening');
    launchTimer.current = window.setTimeout(() => setDirectorLaunch('help'), DIRECTOR_STARTUP_GRACE_MS);
    try { window.location.assign(DIRECTOR_PROTOCOL_URL); }
    catch { setDirectorLaunch('help'); }
  }

  function closeDirectorHelp() {
    window.clearTimeout(launchTimer.current);
    setDirectorLaunch('idle');
  }

  return <div className="home-page">
    <BackgroundMusic />
    <section className="hero">
      <div className="hero-copy">
        <span className="eyebrow"><Radio size={15} />Game night · stories · rehearsal</span>
        <h1>Step into your next <em>adventure.</em></h1>
        <p>Play a murder mystery or other story with family and friends where you get to be part of the adventure. Or use QLine to rehearse with the script out of your hands while private cues keep everyone in the scene.</p>
        <div className="hero-actions">
          <button className="button primary" onClick={onCreate}><Clapperboard size={19} />Play or rehearse together</button>
          <button className="button secondary" onClick={onPractice}><Headphones size={19} />Practice alone</button>
          <button className="button secondary" onClick={onJoin}><ScanLine size={19} />Join a room</button>
        </div>
        <div className="home-script-links"><button className="back-button" onClick={onImport}><BookOpenText size={17} />Add your script</button><button className="back-button" onClick={onLibrary}>Browse saved scenes<ArrowRight size={17} /></button></div>
        {!canHost && windowsDesktop && <div className="web-launch-note"><ExternalLink size={15} /><span>Optional: <a href={DIRECTOR_PROTOCOL_URL} onClick={openDirector}>Open the installed Windows Director</a> for its existing voices.</span></div>}
        <div className="trust-row"><span><i />Private cues on each device</span><span><i />Game night or rehearsal</span><span><i />No recording by default</span></div>
      </div>
      <div className="hero-stage" aria-label="Example QLine roles">
        <div className="stage-glow" /><div className="stage-top"><span>EXAMPLE ROLES</span><strong>Private cues</strong></div>
        <div className="cast-orbit">
          {demoScenario.characters.map((character, index) => <article className={`orbit-card orbit-${index + 1}`} key={character.id} style={{ '--character': character.color }}>
            <div className="avatar">{character.initials}</div><span>{character.name}</span><small>{character.role}</small><i>Ready</i>
          </article>)}
          <div className="director-core"><img src="/icons/icon-192.png" alt="" /><span>PRIVATE CUE</span><strong>Armed</strong></div>
        </div>
        <div className="waveform" aria-hidden="true">{Array.from({ length: 28 }, (_, index) => <i key={index} />)}</div>
      </div>
    </section>
    {!canHost && windowsDesktop && directorLaunch !== 'idle' && <div className="director-launch-backdrop" role="dialog" aria-modal="true" aria-labelledby="director-launch-title">
      <div className="director-launch-card">
        <button className="director-launch-close" onClick={closeDirectorHelp} aria-label="Close Director help"><X size={20} /></button>
        {directorLaunch === 'opening' ? <>
          <span className="director-launch-icon opening"><RefreshCw size={27} /></span>
          <span className="eyebrow">Opening the Windows app</span>
          <h2 id="director-launch-title">QLine is starting.</h2>
          <p>If Windows shows a question, choose <b>Open QLine The Game</b>. The first start can take up to 30 seconds while the private voices load.</p>
          <div className="director-launch-status"><i /><span>Waiting for the Director window…</span></div>
        </> : <>
          <span className="director-launch-icon"><Clapperboard size={27} /></span>
          <span className="eyebrow">The browser did not switch</span>
          <h2 id="director-launch-title">Open QLine from your desktop.</h2>
          <p>This browser may block links to Windows apps. Double-click <b>QLine The Game</b> on your desktop. The desktop icon opens directly to Create Room.</p>
          <div className="director-launch-actions">
            <button className="button primary" onClick={openDirector}><RefreshCw size={17} />Try opening QLine again</button>
          </div>
          <small>If you have a downloaded ZIP and no desktop icon, open its folder and double-click <b>QLine The Game.exe</b> once. Then use Chrome or Edge for the website button.</small>
        </>}
      </div>
    </div>}
  </div>;
}
